'use client';

import { useState, useEffect } from 'react';
import { FileText, FolderOpen, ChevronDown, ChevronRight, Star, Clock } from 'lucide-react';
import { artifacts, Artifact, Project } from './mockData';

type Stage = 'specify' | 'plan' | 'tasks';

interface ArtifactsTabProps {
  phases: Project['phases'];
  selectedStage: Stage | null;
  onArtifactClick?: (artifact: Artifact) => void;
}

export function ArtifactsTab({ phases, selectedStage, onArtifactClick }: ArtifactsTabProps) {
  // Strong color progression: fuchsia → violet → indigo (all vibrant)
  const stages = [
    {
      key: 'specify' as const,
      label: 'Specify',
      description: 'Requirements and user stories',
      bgColor: 'bg-fuchsia-500/10',
      borderColor: 'border-fuchsia-400/40',
      textColor: 'text-fuchsia-400',
      dotColor: 'bg-fuchsia-400',
    },
    {
      key: 'plan' as const,
      label: 'Plan',
      description: 'Architecture and design decisions',
      bgColor: 'bg-violet-500/15',
      borderColor: 'border-violet-500/40',
      textColor: 'text-violet-400',
      dotColor: 'bg-violet-500',
    },
    {
      key: 'tasks' as const,
      label: 'Tasks',
      description: 'Implementation tracking',
      bgColor: 'bg-indigo-600/20',
      borderColor: 'border-indigo-600/40',
      textColor: 'text-indigo-400',
      dotColor: 'bg-indigo-600',
    },
  ];

  // Filter stages based on project phases
  const visibleStages = stages.filter((stage) => {
    const status = phases[stage.key];
    return status === 'complete' || status === 'in-progress';
  });

  return (
    <div className="p-4 space-y-4">
      {visibleStages.map((stage) => (
        <StageSection
          key={stage.key}
          stage={stage}
          artifacts={artifacts[stage.key] || []}
          isHighlighted={selectedStage === stage.key}
          forceExpanded={selectedStage === stage.key}
          onArtifactClick={onArtifactClick}
        />
      ))}
      {visibleStages.length === 0 && (
        <div className="text-center text-text-muted py-8">
          No artifacts available yet
        </div>
      )}
    </div>
  );
}

interface StageSectionProps {
  stage: {
    key: string;
    label: string;
    description: string;
    bgColor: string;
    borderColor: string;
    textColor: string;
    dotColor: string;
  };
  artifacts: Artifact[];
  isHighlighted: boolean;
  forceExpanded: boolean;
  onArtifactClick?: (artifact: Artifact) => void;
}

function StageSection({ stage, artifacts, isHighlighted, forceExpanded, onArtifactClick }: StageSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Force expand when highlighted
  useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    }
  }, [forceExpanded]);

  return (
    <div
      className={`rounded-lg border overflow-hidden transition-all ${
        isHighlighted
          ? `${stage.borderColor} ring-2 ring-offset-2 ring-offset-bg-alt ring-primary/30`
          : stage.borderColor
      }`}
    >
      {/* Background */}
      <div className={stage.bgColor}>
        {/* Section Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors"
        >
          {/* Expand/Collapse Icon */}
          {isExpanded ? (
            <ChevronDown className={`w-4 h-4 ${stage.textColor}`} />
          ) : (
            <ChevronRight className={`w-4 h-4 ${stage.textColor}`} />
          )}

          {/* Stage Indicator */}
          <div className={`w-2.5 h-2.5 rounded-full ${stage.dotColor}`} />

          {/* Label */}
          <div className="flex-1 text-left">
            <span className={`font-medium ${stage.textColor}`}>{stage.label}</span>
            <span className="text-text-muted text-sm ml-2">({artifacts.length} files)</span>
          </div>

          {/* Description */}
          <span className="text-text-muted text-sm hidden sm:block">{stage.description}</span>
        </button>

        {/* Artifacts List */}
        {isExpanded && (
          <div className="px-4 pb-3 space-y-2">
            {artifacts.map((artifact) => (
              <ArtifactCard
                key={artifact.name}
                artifact={artifact}
                stageColor={stage.textColor}
                onClick={onArtifactClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ArtifactCardProps {
  artifact: Artifact;
  stageColor: string;
  onClick?: (artifact: Artifact) => void;
}

function ArtifactCard({ artifact, stageColor, onClick }: ArtifactCardProps) {
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 bg-bg-card/50 rounded-md border border-border/30 hover:border-border/50 transition-colors cursor-pointer group"
      onClick={() => onClick?.(artifact)}
    >
      {/* File Icon */}
      <FileText className={`w-4 h-4 ${artifact.isCore ? stageColor : 'text-text-muted'}`} />

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-text font-mono text-sm truncate">{artifact.name}</span>
          {artifact.isCore && (
            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
          )}
        </div>
        <div className="flex items-center gap-2 text-text-muted text-xs">
          <FolderOpen className="w-3 h-3" />
          <span className="truncate">{artifact.path}</span>
        </div>
      </div>

      {/* Last Updated */}
      {artifact.lastUpdated && (
        <div className="flex items-center gap-1 text-text-muted text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          <Clock className="w-3 h-3" />
          <span>{artifact.lastUpdated}</span>
        </div>
      )}
    </div>
  );
}
